import { Fragment } from 'react'
import { ArrowRight, CalendarClock, CheckCircle2, Clock, Mail, MessageSquare, Send, TriangleAlert } from 'lucide-react'
import { useT } from '@/components/visitone/visitone-provider'
import { ModuleShell } from '@/components/today/module-shell'
import { AnimatedNumber } from '@/components/today/animated-number'
import { formatClock, formatNumber } from '@/components/today/format'
import { useTodayModule } from '@/hooks/use-today-module'
import type { MessagingChannel, MessagingChannelId, TonightPerformance } from '@/lib/bff/today'
import type { TodayModuleProps } from '@/components/today/modules/types'

const CHANNEL_ICON: Record<MessagingChannelId, typeof Mail> = {
  email: Mail,
  sms: MessageSquare,
  'post-show': Send,
}

function channelLabel(channel: MessagingChannelId, t: ReturnType<typeof useT>): string {
  if (channel === 'sms') return t('today.tonight.sms')
  if (channel === 'post-show') return t('today.tonight.postShow')
  return t('today.tonight.email')
}

/*
 * The attendance/volume line, rendered as text ("412 booked · 38 orders ·
 * 6 tables"). Each figure animates once on a polled change; the middot
 * separators are decorative. This is deliberately not a heading — only the
 * module title is.
 */
function VolumeLine({ performance }: { performance: TonightPerformance }) {
  const t = useT()
  const figures: { value: number; unit: string }[] = [
    { value: performance.booked, unit: t('today.tonight.bookedUnit') },
    { value: performance.orders, unit: t('today.tonight.ordersUnit') },
    { value: performance.tables, unit: t('today.tonight.tablesUnit') },
  ]
  return (
    <p role="group" className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-fg" aria-label={t('today.tonight.attendanceLabel')}>
      {figures.map((figure, index) => (
        <Fragment key={figure.unit}>
          {index > 0 ? (
            <span aria-hidden="true" className="text-fg-muted">
              ·
            </span>
          ) : null}
          <span className="inline-flex items-baseline gap-1">
            <AnimatedNumber value={figure.value} format={formatNumber} className="font-semibold text-fg-strong" />
            <span className="text-fg-muted">{figure.unit}</span>
          </span>
        </Fragment>
      ))}
    </p>
  )
}

/*
 * One messaging channel segment. Outcome is always carried by text as well as
 * icon (never colour or icon alone). A channel that has not reached its send
 * point shows an em dash rather than a zero; a non-zero failure count is a link
 * that opens the guest list filtered to this performance and channel's
 * failures.
 */
function MessagingStatus({
  channel,
  onOpenFailures,
}: {
  channel: MessagingChannel
  /** Absent when the caller may not open Guests: the failure count is text, not a link. */
  onOpenFailures?: () => void
}) {
  const t = useT()
  const label = channelLabel(channel.channel, t)
  const Icon = CHANNEL_ICON[channel.channel]

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-fg">
      <Icon aria-hidden="true" className="size-3.5 text-fg-muted" />
      <span className="font-medium">{label}</span>
      {channel.due ? (
        <>
          <span className="inline-flex items-center gap-1 text-fg-muted">
            <CheckCircle2 aria-hidden="true" className="size-3.5" />
            {t('today.tonight.delivered', { count: channel.sent })}
          </span>
          {channel.failed > 0 && !onOpenFailures ? (
            <span className="inline-flex items-center gap-1 font-medium text-danger">
              <TriangleAlert aria-hidden="true" className="size-3.5" />
              {t('today.tonight.failed', { count: channel.failed })}
            </span>
          ) : channel.failed > 0 ? (
            <button
              type="button"
              onClick={onOpenFailures}
              className="inline-flex items-center gap-1 rounded-sm font-medium text-danger underline-offset-2 hover:underline"
              aria-label={t('today.tonight.failedLink', { count: channel.failed, channel: label })}
            >
              <TriangleAlert aria-hidden="true" className="size-3.5" />
              {t('today.tonight.failed', { count: channel.failed })}
            </button>
          ) : null}
        </>
      ) : (
        <span className="text-fg-muted">
          <span aria-hidden="true">&mdash;</span>
          <span className="sr-only">{t('today.tonight.notDue')}</span>
        </span>
      )}
    </span>
  )
}

function PerformanceCard({
  performance,
  onOpenRoute,
  canOpenRoute,
}: {
  performance: TonightPerformance
  onOpenRoute: (route: string) => void
  canOpenRoute: (route: string) => boolean
}) {
  const t = useT()
  const performanceRoute = `/programme/${performance.productionId}/performances/${performance.id}`
  return (
    <li className="flex flex-col gap-3 rounded-md border border-line bg-page p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
            <Clock aria-hidden="true" className="size-3.5" />
            {formatClock(performance.startsAt)}
          </span>
          <p className="mt-1 text-sm font-medium text-fg-strong wrap-anywhere">{performance.production}</p>
          <p className="text-xs text-fg-muted wrap-anywhere">{performance.venue}</p>
        </div>
        {canOpenRoute(performanceRoute) ? (
          <button
            type="button"
            onClick={() => onOpenRoute(performanceRoute)}
            className="inline-flex shrink-0 items-center gap-1 rounded-sm text-sm font-medium text-accent underline-offset-2 hover:underline"
            aria-label={t('today.tonight.viewPerformance', { production: performance.production, venue: performance.venue })}
          >
            {t('today.tonight.view')}
            <ArrowRight aria-hidden="true" className="size-3.5" />
          </button>
        ) : null}
      </div>
      <VolumeLine performance={performance} />
      <div className="flex flex-wrap gap-x-4 gap-y-2 border-t border-line pt-3">
        {performance.messaging.map((channel) => {
          const failuresRoute = `/guests?filter=message-failed&performance=${performance.id}&channel=${channel.channel}`
          return (
            <MessagingStatus
              key={channel.channel}
              channel={channel}
              onOpenFailures={canOpenRoute(failuresRoute) ? () => onOpenRoute(failuresRoute) : undefined}
            />
          )
        })}
      </div>
    </li>
  )
}

export function TonightModule({
  headingId,
  tenantId,
  venue,
  onOpenRoute,
  canOpenRoute,
  onDismiss,
  refusal,
}: TodayModuleProps) {
  const t = useT()
  const title = t('today.moduleTitles.tonight')
  const { data, error, isLoading, retry } = useTodayModule('tonight', tenantId, venue)

  // Order earliest start first. Sorting here (not in the mock) keeps the client
  // authoritative on display order regardless of the source's order.
  const performances = data ? [...data.performances].sort((a, b) => a.startsAt.localeCompare(b.startsAt)) : []

  return (
    <ModuleShell
      title={title}
      headingId={headingId}
      isLoading={isLoading}
      error={error}
      onRetry={retry}
      onDismiss={onDismiss}
      refusal={refusal}
      loadingRows={2}
    >
      {performances.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {performances.map((performance) => (
            <PerformanceCard
              key={performance.id}
              performance={performance}
              onOpenRoute={onOpenRoute}
              canOpenRoute={canOpenRoute}
            />
          ))}
        </ul>
      ) : (
        <div className="flex flex-col items-start gap-2 py-2">
          <p className="text-sm text-fg-muted">{t('today.tonight.noneToday')}</p>
          {data?.nextDated && canOpenRoute(`/programme/${data.nextDated.id}`) ? (
            <button
              type="button"
              onClick={() => onOpenRoute(`/programme/${data.nextDated?.id}`)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline"
            >
              <CalendarClock aria-hidden="true" className="size-4" />
              {t('today.tonight.viewNext')}
            </button>
          ) : null}
        </div>
      )}
    </ModuleShell>
  )
}
